/**
 * @quotech/ui — FilterChip
 *
 * Pill-shaped filter button. Mirrors the bills status-filter pill styling
 * (rounded-full border px-2.5 py-0.5 text-xs) so the many hand-rolled
 * status/filter pills across hubs can converge on one primitive during
 * the P2 migration.
 *
 * FLOW: props (active/count/tone + native button attrs) → cn() class merge → <button>
 *
 * USAGE:
 *   import { FilterChip } from '@quotech/ui'
 *   <FilterChip active={status === 'all'} count={total} onClick={() => setStatus('all')}>
 *     All
 *   </FilterChip>
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/utils'

/** Visual tone for the chip. `default` is the neutral/primary pill;
 * `danger` is the rose review-queue chip (held-back / needs-attention). */
export type FilterChipTone = 'default' | 'danger'

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
  /** Optional trailing count badge (e.g. how many items match this filter). */
  count?: number
  /** Colour tone. Defaults to `default` (primary pill styling). */
  tone?: FilterChipTone
}

// Active/inactive class pairs per tone. `default` is the original primary
// pill; `danger` reproduces the bills review-queue rose chip verbatim so the
// two hand-rolled bills groups can converge on this one primitive.
const TONE_CLASSES: Record<FilterChipTone, { active: string; inactive: string }> = {
  default: {
    active: 'border-primary bg-primary text-primary-foreground',
    inactive: 'border-border bg-background text-muted-foreground hover:bg-accent',
  },
  danger: {
    active: 'border-rose-500 bg-rose-500 text-white',
    inactive:
      'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300',
  },
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ className, active = false, count, tone = 'default', children, type = 'button', ...props }, ref) => {
    const toneClass = TONE_CLASSES[tone]
    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={active}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition',
          active ? toneClass.active : toneClass.inactive,
          className,
        )}
        {...props}
      >
        {children}
        {count != null && <span className="tabular-nums opacity-70">{count}</span>}
      </button>
    )
  },
)
FilterChip.displayName = 'FilterChip'
